import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import EditIcon from "@mui/icons-material/Edit";
import { useParams } from "react-router-dom";
import axios from "axios";
import OriginalPhotos from "./OriginalPhotos";

function UserDetails() {
  const clientMongoId = localStorage.getItem("clientMongoId");
  const { id: userId } = useParams();
  const [user, setUser] = useState(null);
  const [folders, setFolders] = useState([]);
  const [addDialog, setAddDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [showOriginalPhotos, setShowOriginalPhotos] = useState(false);
  const [showEventCopyPhotos, setShowEventCopyPhotos] = useState(false);
  const [eventCopyImages, setEventCopyImages] = useState([]);
  const [showFramePhoto, setShowFramePhoto] = useState(false);
  const [framePhotoImages, setFramePhotoImages] = useState([]);
  const [previewImg, setPreviewImg] = useState(null); // Add this state at the top
  const [editField, setEditField] = useState(null); // "username", "password", or "mobile"
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch user and folders
  useEffect(() => {
    async function fetchUser() {
      const res = await axios.get(`https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}`);
      setUser(res.data);
      let dbFolders = res.data.folders || [];
      const eventCopy = res.data.event ? `${res.data.event} Copy` : "Event Copy";
      // Remove "Original Photos", eventCopy, and framephoto if present
      dbFolders = dbFolders.filter(f => f !== "Original Photos" && f !== eventCopy && f !== "framephoto");
      setFolders(["Original Photos", eventCopy, "framephoto", ...dbFolders]);
    }
    fetchUser();
  }, [userId, clientMongoId]);

  // Handle add folder flow
  const handleAddFolder = () => setConfirmDialog(true);
  const handleConfirmYes = () => {
    setConfirmDialog(false);
    setAddDialog(true);
  };
  const handleConfirmNo = () => setConfirmDialog(false);

  // Add new folder
  const handleCreateFolder = async () => {
    if (newFolderName.trim()) {
      try {
        const res = await axios.post(
          `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${user._id}/folders`,
          { folderName: newFolderName }
        );
        setFolders(res.data); // Update folders from DB response
        setNewFolderName("");
        setAddDialog(false);
      } catch (err) {
        // handle error
      }
    }
  };

  const handleEdit = (field) => {
    setEditField(field);
    setEditValue(user[field]);
  };

  const handleEditChange = (e) => setEditValue(e.target.value);

  const handleEditCancel = () => {
    setEditField(null);
    setEditValue("");
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${user._id}`,
        { [editField]: editValue }
      );
      setUser(res.data); // Update user with new data
      setEditField(null);
      setEditValue("");
    } catch (err) {
      // Optionally show error
    }
    setSaving(false);
  };

  if (showOriginalPhotos && user) {
    return <OriginalPhotos eventName={user.event} userId={user._id} />;
  }

  if (!user) return <Typography>Loading...</Typography>;

  return (
    <Box sx={{ pt: 10, px: 2, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Paper
        sx={{
          maxWidth: 1500,
          mx: "auto",
          mb: 4,
          borderRadius: 5,
          boxShadow: 6,
          p: { xs: 2, sm: 3, md: 5 },
          background: "linear-gradient(135deg, #e3f2fd 0%, #fffde7 100%)"
          // Removed fixed height!
        }}
      >
        <Grid container spacing={4} alignItems="flex-start">
          {/* Table section */}
          <Grid item xs={12} md={4.8}>
            <Box sx={{ mb: 3, ml: { xs: 0, md: 5 } }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "#1976d2",
                 fontFamily:"Josefin Sans",
                  letterSpacing: 1.5,
                  width: { xs: "100%", md: 500 },
                  wordBreak: "break-word"
                }}
              >
                {user.name}
              </Typography>
              <Typography sx={{ color: "#888", fontSize: 18, wordBreak: "break-word", fontFamily:"Jost", }}>
                {user.event}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 3, bgcolor: "#fff" }}>
              <Table >
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#1976d2" , fontFamily:"Josefin Sans",}}>User ID</TableCell>
                    <TableCell >{user.userId}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#1976d2", fontFamily:"Josefin Sans", }}>Username</TableCell>
                    <TableCell sx={{ fontFamily:"Josefin Sans",}}>
                      {editField === "username" ? (
                        <>
                          <TextField
                            value={editValue}
                            onChange={handleEditChange}
                            size="small"
                            sx={{ mr: 1, width: 140, fontFamily:"Josefin Sans", }}
                          />
                          <Button  sx={{ fontFamily:"Josefin Sans",}}onClick={handleEditSave} size="small" variant="contained" color="primary" disabled={saving}>
                            Save
                          </Button>
                          <Button onClick={handleEditCancel} size="small" sx={{ ml: 1, fontFamily:"Josefin Sans", }}>
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          {user.username}
                          <IconButton onClick={() => handleEdit("username")} size="small" sx={{ ml: 1, fontFamily:"Josefin Sans", }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#1976d2", fontFamily:"Josefin Sans", }}>Password</TableCell>
                    <TableCell sx={{ fontFamily:"Josefin Sans",}}>
                      {editField === "password" ? (
                        <>
                          <TextField
                            value={editValue}
                            onChange={handleEditChange}
                            size="small"
                            sx={{ mr: 1, width: 140 }}
                          />
                          <Button sx={{ fontFamily:"Josefin Sans",}} onClick={handleEditSave} size="small" variant="contained" color="primary" disabled={saving}>
                            Save
                          </Button>
                          <Button onClick={handleEditCancel} size="small" sx={{ ml: 1 , fontFamily:"Josefin Sans",}}>
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          {user.password}
                          <IconButton onClick={() => handleEdit("password")} size="small" sx={{ ml: 1 }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#1976d2", fontFamily:"Josefin Sans", }}>Mobile</TableCell>
                    <TableCell sx={{ fontFamily:"Josefin Sans",}}>
                      {editField === "mobile" ? (
                        <>
                          <TextField
                            value={editValue}
                            onChange={handleEditChange}
                            size="small"
                            sx={{ mr: 1, width: 140 }}
                          />
                          <Button sx={{ fontFamily:"Josefin Sans",}} onClick={handleEditSave} size="small" variant="contained" color="primary" disabled={saving}>
                            Save
                          </Button>
                          <Button onClick={handleEditCancel} size="small" sx={{ ml: 1, fontFamily:"Josefin Sans", }}>
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          {user.mobile}
                          <IconButton onClick={() => handleEdit("mobile")} size="small" sx={{ ml: 1 }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#1976d2", fontFamily:"Josefin Sans", }}>Place</TableCell>
                    <TableCell sx={{ fontFamily:"Josefin Sans",}}>{user.place}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#1976d2", fontFamily:"Josefin Sans", }}>Status</TableCell>
                    <TableCell sx={{ fontFamily:"Josefin Sans",}}>{user.status}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#1976d2", fontFamily:"Josefin Sans", }}>Date</TableCell>
                    <TableCell sx={{ fontFamily:"Josefin Sans",}}>{user.date ? user.date.slice(0, 10) : ""}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#388e3c", fontFamily:"Josefin Sans", }}>Original Images</TableCell>
                    <TableCell>
                      {Array.isArray(user.originalImages) && user.originalImages.length > 0 ? (
                        <Typography sx={{ color: "#388e3c", fontWeight: 700 , fontFamily:"Josefin Sans",}}>
                          Uploaded
                        </Typography>
                      ) : (
                        <Typography sx={{ color: "#d32f2f", fontWeight: 700 , fontFamily:"Josefin Sans",}}>
                          Not Uploaded
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "#388e3c", fontFamily:"Josefin Sans", }}>Selected Copy</TableCell>
                    <TableCell>
                      {Array.isArray(user.selectedCopyImages) && user.selectedCopyImages.length > 0 ? (
                        <Typography sx={{ color: "#388e3c", fontWeight: 700, fontFamily:"Josefin Sans", }}>
                          Copied
                        </Typography>
                      ) : (
                        <Typography sx={{ color: "#d32f2f", fontWeight: 700, fontFamily:"Josefin Sans", }}>
                          Not Selected
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          {/* Albums, Frames, Folders */}
          <Grid item xs={12} md={7.2}>
            <Paper
              sx={{
                p: { xs: 2, sm: 3, md: 5 },
                borderRadius: 4,
                ml: { md: 7, xs: 0 },
                boxShadow: 2,
                background: "linear-gradient(135deg, #e3f2fd 0%, #e8f5e9 100%)",
                mb: 3,
                mt: { xs: 0, md: 5 },
                width:{ xs: "250px", md: "770px" },
                height:{sm:"" ,md:"260px"},
                // No width property!
              }}
            >
              {/* Albums */}
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1976d2", mb: 2, fontFamily:"Josefin Sans", }}>
                Selected Albums
              </Typography>
              {user.albumselected && user.albumselected.length > 0 ? (
                <Grid container spacing={2} mb={3}>
                  {user.albumselected.map((album, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Paper
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: "#e3f2fd",
                          minWidth: 100,
                          textAlign: "center",
                          boxShadow: 1,
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: 14, sm: 16 }, fontFamily:"Jost", }}>
                          {album}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography sx={{ color: "#888", mb: 3, fontFamily:"Jost", }}>No albums selected.</Typography>
              )}
              <Divider sx={{ my: 2 }} />
              {/* Frames */}
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1976d2", mb: 2, fontFamily:"Josefin Sans", }}>
                Selected Frames
              </Typography>
              {user.frameselected && user.frameselected.length > 0 ? (
                <Grid container spacing={2}>
                  {user.frameselected.map((frame, idx) => (
                    <Grid item xs={12} sm={6} md={3} key={idx}>
                      <Paper
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: "#fffde7",
                          minWidth: 100,
                          textAlign: "center",
                          boxShadow: 1
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: 14, sm: 16 }, fontFamily:"Jost", }}>
                          {frame}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography sx={{ color: "#888" }}>No frames selected.</Typography>
              )}
            </Paper>
            {/* Folders below albums/frames */}
            <Grid container spacing={2} alignItems="center" wrap="wrap" sx={{ mt: 0 }}>
              {folders.map((folder, idx) => (
                <Grid item xs={12} sm={6} md={4} key={folder}>
                  <Paper
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: { xs: 2, sm: 3, md: 5 },
                      ml: { xs: 0, md: 5 },
                      borderRadius: 3,
                      boxShadow: 3,
                      bgcolor: idx === 0 ? "#e3f2fd" : idx === 1 ? "#e8f5e9" : "#fff",
                      cursor: "pointer",
                      minWidth: 100,
                      width: { xs: "100%", md: "70%" },
                      transition: "all 0.2s",
                      "&:hover": {
                        bgcolor: "#fffde7",
                        boxShadow: 6,
                        transform: "scale(1.03)"
                      }
                    }}
                    onClick={
                      idx === 0
                        ? () => setShowOriginalPhotos(true)
                        : idx === 1
                          ? async () => {
                              // Event Copy logic
                              const res = await axios.get(`https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}`);
                              const images = Array.isArray(res.data.images) ? res.data.images : [];
                              const eventCopy = user.event ? `${user.event} Copy` : "Event Copy";
                              const copiedImages = images.filter(img => img.folder === eventCopy && img.copy === true);
                              setEventCopyImages(copiedImages);
                              setShowEventCopyPhotos(true);
                            }
                          : idx === 2
                            ? async () => {
                                // FramePhoto logic
                                const res = await axios.get(`https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}`);
                                const images = Array.isArray(res.data.images) ? res.data.images : [];
                                const frameImages = images.filter(img => img.folder === "framephoto");
                                setFramePhotoImages(frameImages);
                                setShowFramePhoto(true);
                              }
                            : undefined
                    }
                  >
                    <FolderIcon sx={{ color: idx === 0 ? "#1976d2" : idx === 1 ? "#388e3c" : "#ffa726" }} />
                    <Typography sx={{ ml: 2, fontWeight: 700, fontSize: 18, fontFamily:"Josefin Sans", }}>
                      {folder}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Event Copy Photos Dialog */}
      <Dialog
        open={showEventCopyPhotos}
        onClose={() => setShowEventCopyPhotos(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {user.event ? `${user.event} Copy` : "Event Copy"} &nbsp;
          <Typography component="span" sx={{ fontSize: 18, color: "#a67c52", fontWeight: 700, fontFamily:"Josefin Sans", }}>
            ({eventCopyImages.length} images)
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            minHeight: "60vh", // Increase height
            maxHeight: "80vh", // Allow scroll if too many images
            overflowY: "auto"
          }}
        >
          <Grid container spacing={2}>
            {eventCopyImages.length === 0 && (
              <Typography sx={{ color: "#a67c52", m: 2, fontFamily:"Josefin Sans", }}>No copied images found.</Typography>
            )}
            {eventCopyImages.map((img, idx) => (
              <Grid item xs={12} sm={6} md={2.4} key={img._id || idx}>
                <Paper sx={{ p: 1, borderRadius: 2, boxShadow: 2, textAlign: "center" }}>
                  <img
                    src={img.src}
                    alt={img.folder}
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 8,
                      marginBottom: 8,
                      border: "2px solid #f5e1c1",
                      cursor: "pointer"
                    }}
                    onClick={() => setPreviewImg(img.src)}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!previewImg}
        onClose={() => setPreviewImg(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
            p: 2
          }}
        >
          <img
            src={previewImg}
            alt="Preview"
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "80vh",
              borderRadius: 12,
              objectFit: "contain"
            }}
          />
        </DialogContent>
      </Dialog>

      {/* FramePhoto Dialog */}
      <Dialog
        open={showFramePhoto}
        onClose={() => setShowFramePhoto(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          FramePhoto&nbsp;
          <Typography component="span" sx={{ fontSize: 18, color: "#a67c52", fontWeight: 700 , fontFamily:"Josefin Sans",}}>
            ({framePhotoImages.length} images)
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            minHeight: "60vh",
            maxHeight: "80vh",
            overflowY: "auto"
          }}
        >
          <Grid container spacing={2}>
            {framePhotoImages.length === 0 && (
              <Typography sx={{ color: "#a67c52", m: 2, fontFamily:"Josefin Sans", }}>No frame photos found.</Typography>
            )}
            {framePhotoImages.map((img, idx) => (
              <Grid item xs={12} sm={6} md={2.4} key={img._id || idx}>
                <Paper sx={{ p: 1, borderRadius: 2, boxShadow: 2, textAlign: "center" }}>
                  <img
                    src={img.src}
                    alt={img.folder}
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 8,
                      marginBottom: 8,
                      border: "2px solid #f5e1c1",
                      cursor: "pointer"
                    }}
                    onClick={() => setPreviewImg(img.src)}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default UserDetails;